<template>
  <div class="w-[100%]">
    <a-collapse
      v-if="watchFaces.length && categorize"
      v-model:activeKey="activeKey"
      ghost
      class="my-4"
    >
      <template #expandIcon="{ isActive }">
        <div
          class="duration-200"
          :style="{
            transform: `translate(0, ${isActive ? -6 : -4}px) rotate(${
              isActive ? 0 : -90
            }deg)`
          }"
        >
          <Icon
            :width="12"
            :height="12"
            icon="collapse-expand.svg"
            fill="#FFF"
          />
        </div>
      </template>
      <a-collapse-panel
        v-for="(categoryItems, category) in categorizedWatchfaces"
        :key="category"
        :name="category"
        :header="getProductNameIfNeed(category as WatchfaceDeviceType)"
      >
        <div class="_grid">
          <div v-for="(item, key) in categoryItems" :key="key">
            <DropDown
              :trigger="'contextmenu'"
              :menus="
                item.isSample ? [] : contextmenus(item.path, item.collect)
              "
            >
              <watchface-item
                :watchface="item"
                :preview="previewImgs[item.path]"
                :input-search-value="inputSearchValue"
                @open="_open"
                @change-collect="changeCollect"
                @rename="onRename"
              />
            </DropDown>
          </div>
        </div>
      </a-collapse-panel>
    </a-collapse>
    <div
      v-else-if="watchFaces.length && !categorize"
      :class="{ 'px-5 py-2 my-8': !compactUncategorized }"
    >
      <div class="_grid">
        <template v-for="categoryItems in categorizedWatchfaces"
          ><div v-for="(item, key) in categoryItems" :key="key">
            <DropDown
              :trigger="'contextmenu'"
              :menus="
                item.isSample ? [] : contextmenus(item.path, item.collect)
              "
            >
              <watchface-item
                :watchface="item"
                :preview="previewImgs[item.path]"
                :input-search-value="inputSearchValue"
                @open="_open"
                @change-collect="changeCollect"
                @rename="onRename"
              />
            </DropDown></div
        ></template>
      </div>
    </div>
    <div v-else class="flex justify-center items-center h-56">
      <div v-if="inputSearchValue.length">未找到表盘</div>
      <div v-else>
        {{
          {
            lasted: '无最近打开表盘',
            collect: '无收藏表盘',
            sample: '无表盘样例'
          }[menuType]
        }}
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  HistoryWatchfaceItem,
  useWatchfaceHistory
} from '@/hooks/use-watchface-history';
import { useWatchfaceManage } from '@/hooks/use-watchface-manage';
import { ElectronApi } from '@/services/electron.api';
import { WatchfaceService } from '@/services/watchface';
import { Modal, notification } from 'ant-design-vue';
import { SampleService } from '@/services/sample.service';
import { computed, onMounted, ref, watch } from 'vue';
import { validWatchfaceDir } from '@/utils/watchface-valid.utils';
import { uniqueFileName } from '@/utils/fs/name.utils';
import { WatchfaceDeviceType, useWatchfaceDevices } from '@/hooks';

const isDev = import.meta.env.DEV;

const props = defineProps<{
  menuType: 'lasted' | 'collect' | 'sample';
  inputSearchValue: string;
  watchFaces: HistoryWatchfaceItem[];
  categorize: boolean;
  compactUncategorized: boolean;
  previewImgs: Record<string, string>;
}>();

const emits = defineEmits<{
  (event: 'updateWatchface', path: string): void;
}>();
const { deleteFromStorage, updateItem, addItem, updateItems } =
  useWatchfaceHistory();
const { gotoWachfaceEditor, cloneWatchface } = useWatchfaceManage();

const categorizedWatchfaces = computed(() => {
  const categorized: { [key: string]: Array<HistoryWatchfaceItem> } = {};
  const deviceTypeArr = [];
  for (const item of props.watchFaces) {
    const deviceType = item.deviceType || 'Uncategorized';
    if (!categorized[deviceType]) {
      categorized[deviceType] = [];
    }
    deviceTypeArr.push(deviceType);
    categorized[deviceType].push(item);
  }
  const _deviceTypeArr = [...new Set(deviceTypeArr)].sort();
  const _categorized: { [key: string]: Array<HistoryWatchfaceItem> } = {};
  _deviceTypeArr.forEach((deviceType) => {
    _categorized[deviceType] = categorized[deviceType];
  });
  return _categorized;
});

const _openNormalWatchface = async (watchfacePath: string) => {
  try {
    await validWatchfaceDir(watchfacePath); //验证资源的合法性
    await gotoWachfaceEditor(watchfacePath); //根据path初始化表盘
  } catch (e) {
    notification.error({ message: e as string });
    deleteFromStorage(watchfacePath);
  }
};

const _openSampleWatchface = async (samplePath: string) => {
  // 首先表盘移动到默认目录中
  const fs = ElectronApi.fs;
  const path = ElectronApi.path;
  const dir = await WatchfaceService.getSaveDir();
  if (!fs || !path || !dir) return;
  const service = new WatchfaceService(samplePath);
  const desc = await service.description.getDescriptionData();
  const name = await uniqueFileName(dir, 'sample_' + (desc?.name ?? ''));
  if (!name) return;
  const watchfacePath = path.join(dir, name);

  await fs.promises.cp(samplePath, watchfacePath, { recursive: true });
  await _openNormalWatchface(watchfacePath);
};

const _open = async (path: string) => {
  if (props.menuType == 'sample') {
    await _openSampleWatchface(path);
  } else {
    await _openNormalWatchface(path);
  }
};

const contextmenus = (path: string, collect?: boolean) => {
  const res: (DropDownMenuItem & { hidden?: boolean })[] = [
    { title: '打开', onClick: () => _open(path) },
    { delimiter: true },
    {
      title: '创建副本',
      onClick: async () => {
        const path2 = await cloneWatchface(path);
        if (path2) addItem(path2);
      },
      hidden: props.menuType == 'collect'
    },
    {
      title: '打开文件所在位置',
      onClick: () => ElectronApi.shell?.showItemInFolder(path)
    },
    { delimiter: true },
    {
      title: '编辑表盘信息',
      onClick: () => {
        updateWatchface(path);
      }
    },
    {
      title: collect ? '取消收藏' : '收藏',
      onClick: () => {
        changeCollect(path, !collect);
      }
    },
    { delimiter: true, hidden: props.menuType == 'collect' },
    {
      title: '从列表移除',
      onClick: () => {
        deleteFromStorage(path);
      },
      hidden: props.menuType == 'collect'
    }
  ];
  if (isDev) {
    res.push({
      title: '导出为表盘样例（DEV）',
      onClick: async () => {
        try {
          const zipfile = await new SampleService().exportSample(path);
          if (!zipfile) throw '导出失败';
          Modal.confirm({
            title: '表盘导出成功',
            content: '是否打开样例文件',
            okText: '打开',
            cancelText: '取消',
            onOk() {
              ElectronApi.showItemInFolder(zipfile);
            }
          });
        } catch (error) {
          notification.error({
            message: '表盘导出失败'
          });
        }
      }
    });
  }
  return res.filter((item) => !item.hidden);
};

const updateWatchface = (path: string) => {
  emits('updateWatchface', path);
};
const changeCollect = (path: string, collect: boolean) => {
  // 最多可收藏50个表盘
  const collected = props.watchFaces.filter((item) => !!item.collect);
  if (collect && collected.length >= 50) {
    notification.warning({
      message: '无法收藏',
      description: '最多收藏50个表盘'
    });
    return;
  }
  updateItem(path, { collect });
};

// 修改名称
const onRename = async (path: string, val: string) => {
  const service = new WatchfaceService(path);
  const description = await service.description.getDescriptionData();
  if (!description) return;
  description.name = val;
  await service.description.writeDecsriptionXML(description);
  await updateItems();
};

//折叠面板的key
const activeKey = ref<string[]>([]);
// 第一次进来就打开所有折叠面板
watch(categorizedWatchfaces, (newCategorizedWatchfaces) => {
  activeKey.value = Object.keys(newCategorizedWatchfaces);
});
onMounted(() => {
  activeKey.value = Object.keys(categorizedWatchfaces.value ?? {});
});

// 产品名称
const { getProductNameIfNeed } = useWatchfaceDevices();
</script>

<style scoped lang="less">
._grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 208px);
  column-gap: 30px;
  row-gap: 20px;
}

:deep(.ant-collapse) {
  .ant-collapse-item > .ant-collapse-header {
    display: flex;
    margin: 4px 20px;
    padding: 4px 16px;
    font-size: 20px;
    font-weight: 500;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 1;
    border: 2px solid transparent;
    &:hover {
      border-radius: 8px;
      border: 2px solid #0d84f2;
    }
    .ant-collapse-arrow {
      margin-left: -11px;
      margin-right: 5px !important;
    }
    .ant-collapse-header-text {
      margin-top: 4px; // 调整文字的上边距
    }
  }
  .ant-collapse-content-box {
    padding: 8px 20px 24px 20px !important;
  }
}
</style>
